/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { InputAction } from "../../enums/InputAction";
import { Log } from "../../logging/Log";
import type { TInputEvent, InputInterceptor } from "../types/TInputEvent";

const TAG: string = "DirectionalCodeInterceptor";

/**
 * Maximum gap (ms) between consecutive presses of the sequence.
 *
 * Much tighter than `SecretCodeInterceptor`'s five seconds, and
 * deliberately so. A digit code cannot be typed by accident while using
 * the app; a directional sequence is made of the same four keys ordinary
 * navigation uses, so the only thing separating "the user meant this" from
 * "the user was moving around a grid" is cadence. 1.2 s is comfortably
 * above a deliberate press-press-press and below browsing speed.
 */
const STEP_TIMEOUT_MS: number = 1200;

/** D-pad actions the interceptor tracks; everything else resets nothing. */
const DIRECTIONAL_ACTIONS: ReadonlySet<InputAction> = new Set<InputAction>([
  InputAction.NAV_UP,
  InputAction.NAV_DOWN,
  InputAction.NAV_LEFT,
  InputAction.NAV_RIGHT
]);

/**
 * Watch for a D-pad sequence (e.g. UP UP DOWN DOWN RIGHT) and fire on it.
 *
 * ## Why this is not `createSecretCodeInterceptor`
 *
 * That one matches digits and **consumes every digit it sees**, which is
 * safe because nothing else in the app listens for bare number keys during
 * normal navigation. Directional keys are the opposite case: they are the
 * app's primary navigation, so this interceptor passes every press through
 * untouched and consumes **only** the final press that completes the
 * sequence — otherwise arming the code would freeze the screen underneath.
 *
 * Consuming that last press is the right trade: the alternative is a stray
 * cursor move at the same instant the overlay appears.
 *
 * Matching uses the same sliding window as the digit interceptor, so a
 * false start (UP UP UP DOWN DOWN RIGHT) still matches on the suffix
 * rather than needing a clean restart.
 *
 * @param sequence - The D-pad actions to match, in order. Must be non-empty.
 * @param onMatch - Invoked once when the full sequence completes in time.
 * @returns An interceptor to register on the NavigationEngine.
 */
export function createDirectionalCodeInterceptor(
  sequence: ReadonlyArray<InputAction>,
  onMatch: () => void
): InputInterceptor {
  let buffer: Array<InputAction> = [];
  let lastTimestamp: number = 0;

  const matchesPrefix = (candidate: ReadonlyArray<InputAction>): boolean => {
    for (let i: number = 0; i < candidate.length; i += 1) {
      if (candidate[i] !== sequence[i]) {
        return false;
      }
    }
    return true;
  };

  return (event: TInputEvent): boolean | void => {
    if (sequence.length === 0 || event.eventType !== "keydown") {
      return;
    }
    if (!DIRECTIONAL_ACTIONS.has(event.action)) {
      return;
    }
    // A held key produces a stream of repeats; counting them would let a
    // single long press satisfy "UP UP" on its own.
    if (event.repeat) {
      return;
    }

    const now: number = event.timestamp;
    if (buffer.length > 0 && now - lastTimestamp > STEP_TIMEOUT_MS) {
      buffer = [];
    }
    lastTimestamp = now;

    buffer.push(event.action);
    while (buffer.length > 0 && !matchesPrefix(buffer)) {
      buffer = buffer.slice(1);
    }

    if (buffer.length !== sequence.length) {
      return;
    }

    Log.i(TAG, "createDirectionalCodeInterceptor(): sequence matched");
    buffer = [];
    onMatch();
    return true;
  };
}
